import { z } from 'zod';
import { optionalText, requiredText } from '../common/coercion.js';
/**
 * Locations de Janis (`POST commerce/api/location`): es la unica via por la que
 * el middleware puede crear una bodega en Janis.
 *
 * ## Como llegamos aca (verificado en vivo 2026-09-02, QA `janisqa.in`, cliente `amoble`)
 *
 * Un host POR SERVICIO (`https://<servicio>.<dominio>/api/<recurso>`) y los tres
 * headers estaticos de siempre. Lo importante es el camino que NO sirve:
 *
 * - `POST wms/api/warehouse` responde **401**, aunque el perfil de la api-key
 *   sea Super Administrador y tenga `wms:warehouse:write`. Las bodegas del WMS
 *   se llaman **almacenes** y no se crean por ahi.
 * - Ese 401 lo emite el autorizador (API Gateway) y su cuerpo es **identico** al
 *   de unas credenciales malas (ver `janisAuthorizerErrorSchema` en
 *   `./errors.js`): no dice "te falta permiso", asi que es facil perder un rato
 *   revisando la api-key cuando el problema es la ruta.
 * - `POST commerce/api/location` **si esta permitido**, y en la consola el
 *   formulario de Location trae una seccion **INVENTARIOS**: el almacen se crea
 *   DENTRO de la ubicacion. De ahi el diseno: una location por bodega de SAP.
 *
 * ## De donde salio la lista de obligatorios
 *
 * `name`, `referenceId`, `latitude`, `longitude`, `timezone` y `address` son
 * obligatorios porque **los enumeramos uno por uno con los propios errores de
 * Janis**: se manda el cuerpo, Janis rechaza nombrando el campo que falta, se
 * agrega, se repite. No es una lectura de documentacion — la doc publica de
 * Janis no entrega bodies — es la respuesta del ambiente.
 *
 * Consecuencia de ese metodo: sabemos que campos son obligatorios en la RAIZ,
 * pero **no cual de los campos DENTRO de `address` exige Janis**, porque la
 * enumeracion se detuvo cuando `address` completa fue aceptada. Ver
 * `janisLocationAddressSchema`.
 *
 * ## `timezone`, con z minuscula
 *
 * VERIFICADO: el campo es `timezone`, **no** `timeZone`. Como este schema es
 * `strict` (lo EMITIMOS nosotros), escribirlo mal da dos issues locales —
 * `timezone` ausente y `timeZone` no reconocido — en vez de un 400 de Janis
 * quince minutos despues.
 *
 * ## El campo de los almacenes: `warehouses`, y era un supuesto equivocado
 *
 * Suponiamos que se llamaba `inventories`, porque asi lo rotula el formulario de
 * la consola. **Se llama `warehouses` y es OBLIGATORIO.** Lo descubrimos al
 * intentar crear BOD1 de verdad: Janis respondio 400 nombrando el campo y
 * dictando la forma de cada elemento. La conjetura habria costado caro justo
 * como estaba previsto —location sin almacen, fallo tardio en el `stock-batch`—
 * si no fuera que aca el campo es obligatorio y el error salta al crear.
 *
 * Igual se conserva la RELECTURA de la location despues de crearla: confirmar
 * que el almacen quedo adentro es mas barato que descubrirlo semanas despues, y
 * queda como otra entrada del message log.
 *
 * ## El nudo con el stock: `referenceId`
 *
 * `referenceId` es el que ata todo. El middleware publica stock con
 * `warehouseReferenceId` (ver `./stock.js`), o sea con NUESTROS codigos, para no
 * mantener al dia un mapeo de objectId internos de Janis. Para que eso funcione,
 * la location y su almacen se crean con el **`WarehouseCode` de SAP** como
 * `referenceId`. Un `referenceId` distinto al codigo de SAP no rompe la creacion:
 * rompe el `stock-batch` despues.
 *
 * ## Message log
 *
 * Cada `POST` de location se persiste antes de salir, con `system: 'janis'`,
 * `direction: 'outbound'` y el mismo `correlationId` de la corrida que leyo el
 * catalogo de SAP (ver `../sap/warehouse.js`). Es lo que permite abrir la
 * sincronizacion en la UI de monitoreo y leerla completa: que bodegas se
 * descubrieron y cual de ellas se creo, con su payload crudo.
 */
/** Latitud de la ubicacion. Janis la exige y la exige como numero. */
const latitudeSchema = z
    .number({ invalid_type_error: 'La latitud debe ser un numero, no un texto.' })
    .min(-90, 'La latitud no puede ser menor que -90.')
    .max(90, 'La latitud no puede ser mayor que 90.');
/** Longitud de la ubicacion. */
const longitudeSchema = z
    .number({ invalid_type_error: 'La longitud debe ser un numero, no un texto.' })
    .min(-180, 'La longitud no puede ser menor que -180.')
    .max(180, 'La longitud no puede ser mayor que 180.');
/**
 * Zona horaria IANA. El nombre del campo es `timezone`, con **z minuscula**.
 *
 * No se valida contra un catalogo: los identificadores IANA validos incluyen
 * formas de dos y tres segmentos (`America/Santiago`,
 * `America/Argentina/Buenos_Aires`) y un regex casero rechazaria valores
 * legitimos. El valor que usamos esta en `JANIS_LOCATION_DEFAULT_TIMEZONE`.
 */
const timezoneSchema = requiredText(64);
/** Zona horaria de todas las bodegas del grupo. */
export const JANIS_LOCATION_DEFAULT_TIMEZONE = 'America/Santiago';
/**
 * Direccion de la location.
 *
 * VERIFICADO que `address` es obligatoria y que Janis acepta un objeto con estas
 * claves. NO VERIFICADO cual de ellas exige: la enumeracion de errores llego
 * hasta "falta `address`" y se detuvo cuando el objeto completo fue aceptado.
 *
 * Ante esa duda, obligatorio solo lo que una direccion chilena siempre tiene
 * (`country`, `state`, `city`, `street`) y opcional el resto. La asimetria es
 * deliberada, porque los dos errores no cuestan lo mismo:
 *
 * - Marcar opcional algo que Janis exige -> falla el POST con un mensaje de
 *   Janis que **nombra el campo**, queda en el message log y se arregla con una
 *   linea aca.
 * - Marcar obligatorio algo que Janis no exige -> hay que inventar un valor.
 *   `neighborhood` es el caso concreto: en Chile no se usa barrio en la
 *   direccion, y rellenarlo con "Sin barrio" para las 40 bodegas es meter datos
 *   falsos en un sistema de terceros para satisfacer un schema nuestro.
 *
 * Se cierra igual que se abrio: enumerando los errores de Janis, quitando un
 * campo por vez.
 */
export const janisLocationAddressSchema = z
    .object({
    /** Pais. Texto opaco: no esta verificado si espera nombre o ISO 3166-1. */
    country: requiredText(64),
    /** Region / estado. En Chile, la region de la bodega. */
    state: requiredText(64),
    city: requiredText(128),
    street: requiredText(255),
    /** Numero. Opcional: en Chile suele venir dentro de la calle. */
    number: optionalText(32),
    /** Barrio. Opcional a proposito: ver el docblock. */
    neighborhood: optionalText(128),
    /** `ZipCode` de la bodega en SAP, cuando lo hay. */
    postalCode: optionalText(16),
})
    // EMITIDO por nosotros: estricto. Un campo que no esta en el contrato es un
    // error NUESTRO y tiene que verse antes de salir, no llegar a Janis.
    .strict();
/**
 * Almacen (bodega del WMS) que se crea DENTRO de la location.
 *
 * **VERIFICADO el 2026-09-02**, y el nombre del campo NO era el que suponiamos.
 * Se llama `warehouses`, no `inventories`, y es OBLIGATORIO. Lo dijo Janis con su
 * propio mensaje de error, que ademas dicto la forma completa de cada elemento:
 *
 *   Expected a value of type
 *   `[{name,referenceId,group,salesChannels,tasks,limitedToSellers,status}]`
 *   for `main.warehouses` but received `undefined`.
 *
 * Es el almacen del WMS creado dentro de la ubicacion: la unica via, porque
 * `POST wms/api/warehouse` responde 401.
 */
export const janisLocationWarehouseSchema = z
    .object({
    name: requiredText(255),
    /**
     * `WarehouseCode` de SAP. Es el valor que despues viaja como
     * `warehouseReferenceId` en `POST wms/api/stock-batch`.
     */
    referenceId: requiredText(64),
    /**
     * Estado del almacen. La primera linea de este docblock describia una
     * "prioridad numerica" del DOM: era el resto de otro campo que no existe
     * aca, y contradecia al propio `z.enum` de abajo.
     *
     * OBLIGATORIO y con valores dictados por Janis (verificado 2026-09-02):
     * "Expected a value of type `\"active\" | \"inactive\"` for
     *  `main.warehouses.0.status`".
     *
     * Mapea directo desde SAP: `Inactive: "tNO"` -> `active`.
     */
    status: z.enum(['active', 'inactive']),
    /** Los otros campos que Janis enumero en su error. Opcionales hasta confirmar. */
    group: z.string().optional(),
    salesChannels: z.array(z.string()).optional(),
    tasks: z.array(z.string()).optional(),
    limitedToSellers: z.array(z.string()).optional(),
})
    .strict();
/**
 * Cuerpo de `POST commerce/api/location`.
 *
 * Los SIETE campos obligatorios salieron de enumerar los errores del propio
 * Janis, uno por uno (ver el docblock del modulo). El septimo, `warehouses`, se
 * descubrio recien al intentar crear BOD1 de verdad.
 */
export const janisLocationCreateRequestSchema = z
    .object({
    /** Nombre de la ubicacion. Se usa el `WarehouseName` de SAP. */
    name: requiredText(255),
    /**
     * Referencia externa. Va el `WarehouseCode` de SAP: es lo que hace que el
     * `stock-batch` pueda publicar con nuestros propios codigos.
     */
    referenceId: requiredText(64),
    latitude: latitudeSchema,
    longitude: longitudeSchema,
    /** `timezone`, con z MINUSCULA. `timeZone` no existe para Janis. */
    timezone: timezoneSchema,
    address: janisLocationAddressSchema,
    /**
     * Almacenes de la seccion INVENTARIOS. **VERIFICADO y OBLIGATORIO.**
     *
     * Este docblock decia lo contrario —«nombre NO VERIFICADO», «opcional para
     * no bloquear la creacion»— y era un resto del supuesto viejo, cuando
     * creiamos que el campo se llamaba `inventories`. Quedo sin actualizar
     * mientras el modulo entero, el schema del elemento y el propio `.min(1)` de
     * esta linea ya decian lo contrario: el 400 de Janis del 2026-09-02, al
     * crear BOD1, nombra `main.warehouses` y dicta la forma de cada elemento.
     *
     * La contradiccion no era cosmetica: la mitad equivocada invitaba a mandar
     * una location SIN almacen, que Janis rechaza y que ademas no serviria para
     * publicar stock. Se conserva igual la RELECTURA de la location creada, que
     * es lo que confirma que el almacen quedo adentro.
     */
    warehouses: z.array(janisLocationWarehouseSchema).min(1),
})
    .strict();
/**
 * Location tal como la devuelve Janis (respuesta del `POST` y de la relectura).
 *
 * `passthrough` y casi todo opcional porque lo RECIBIMOS de un tercero cuya
 * forma de respuesta no esta verificada: lo unico que se exige es el `id`, que
 * es lo que hace utilizable la respuesta. Estrechar mas mandaria a cuarentena
 * locations que Janis creo de verdad.
 *
 * Los almacenes NO se declaran aca a proposito, y el motivo ya no es una duda
 * sobre el nombre del campo —al ESCRIBIR es `warehouses` y esta verificado—:
 * es que Janis no los devuelve embebidos. La respuesta real de BOD1 trae
 * `warehousesIds` con los ids sueltos, y esa forma no la verificamos mas alla
 * de ese unico caso. La verificacion de "¿quedo el almacen?" se hace sobre el
 * payload crudo, que `passthrough` conserva intacto.
 */
export const janisLocationSchema = z
    .object({
    id: requiredText(64),
    name: optionalText(255),
    referenceId: optionalText(64),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    timezone: optionalText(64),
    address: z
        .object({
        country: optionalText(64),
        state: optionalText(64),
        city: optionalText(128),
        street: optionalText(255),
        number: optionalText(32),
        neighborhood: optionalText(128),
        postalCode: optionalText(16),
    })
        .passthrough()
        .optional(),
})
    .passthrough();
//# sourceMappingURL=location.js.map